import CheckboxFilter from 'components/checkbox-filter/checkbox-filter';
import { PrivateIndividualSearch } from 'components/private-individual-search-filter';
import { ProPresentationSearchFilter } from 'components/pro-presentation-search-filter';
import React, { FunctionComponent, ReactNode } from 'react';
import { Button, Icon, Label } from 'semantic-ui-react';
import styled from 'styled-components';
import { useReviewFilters } from '../hooks/useReviewFilters';

const ActiveFilterChip: FunctionComponent<{
  onRemove: () => void;
  children: ReactNode;
}> = ({ onRemove, children }) => (
  <Button as="div" labelPosition="right" style={{ marginRight: 0 }}>
    <Button color="blue" onClick={onRemove}>
      <Icon name="delete" />
    </Button>
    <Label basic pointing="left">
      {children}
    </Label>
  </Button>
);

const FilterRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;

  /* Some shared filter components (pro search, checkbox filter) ship
     hardcoded margins from the legacy page; neutralize them so spacing
     comes only from the row gap and the row stays flush left. */
  > .ui.search,
  > .ui.button,
  > .ui.buttons,
  > .ui.checkbox {
    margin: 0 !important;
  }
`;

const ViewRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;

  .ui.button,
  .ui.buttons {
    margin: 0;
  }
`;

type ReviewFiltersProps = {
  currentBilikZoneIds?: number[];
  countTodo?: number;
};

export const ReviewFilters: FunctionComponent<ReviewFiltersProps> = ({
  currentBilikZoneIds,
  countTodo,
}) => {
  const { filters, setFilters } = useReviewFilters({
    currentBilikZoneIds,
  });

  const isAllViewActive =
    filters.status === undefined &&
    filters.replyStatus === undefined &&
    (filters.isSpam === undefined || filters.isSpam === null);

  const selectView = (view: {
    status?: string;
    replyStatus?: string;
    isSpam?: boolean;
  }): void => {
    setFilters({
      status: view.status,
      replyStatus: view.replyStatus,
      isSpam: view.isSpam,
      hasCheckerId: undefined,
      page: 1,
    });
  };

  return (
    <>
      <ViewRow>
        <Button.Group>
          <Button
            color="red"
            basic={filters.status !== 'todo'}
            type="button"
            onClick={(): void => selectView({ status: 'todo', isSpam: false })}
          >
            À traiter ({countTodo ?? 0})
          </Button>
        </Button.Group>
        <Button.Group>
          <Button
            basic={!isAllViewActive}
            color="grey"
            type="button"
            onClick={(): void => selectView({})}
          >
            Tous
          </Button>
          <Button
            basic={filters.status !== 'ok'}
            color="grey"
            type="button"
            onClick={(): void => selectView({ status: 'ok', isSpam: false })}
          >
            Validés
          </Button>
          <Button
            basic={filters.status !== 'ko'}
            color="grey"
            type="button"
            onClick={(): void => selectView({ status: 'ko', isSpam: false })}
          >
            Refusés
          </Button>
          <Button
            basic={filters.replyStatus !== 'ko'}
            color="grey"
            type="button"
            onClick={(): void =>
              selectView({ replyStatus: 'ko', isSpam: false })
            }
          >
            Réponse refusée
          </Button>
          <Button
            basic={filters.isSpam !== true}
            color="grey"
            type="button"
            onClick={(): void => selectView({ isSpam: true })}
          >
            Spam
          </Button>
        </Button.Group>
      </ViewRow>
      <FilterRow>
        <ProPresentationSearchFilter
          key={`presentation-${filters.presentationId ?? 'all'}`}
          proPresentationId={
            filters.presentationId !== null ? filters.presentationId : undefined
          }
          setProPresentationId={(value): void => {
            setFilters({
              presentationId: value,
            });
          }}
          setPage={(value): void => {
            setFilters({
              page: value,
            });
          }}
          bilikZoneIds={currentBilikZoneIds}
        />
        {filters.privateIndividualEmail ? (
          <ActiveFilterChip
            onRemove={(): void => {
              setFilters({
                privateIndividualEmail: undefined,
              });
            }}
          >
            {filters.privateIndividualEmail}
          </ActiveFilterChip>
        ) : (
          <PrivateIndividualSearch
            onResultSelect={(event, { result }): void => {
              setFilters({
                privateIndividualEmail: result.value,
                page: undefined,
              });
            }}
            searchContext="proReview"
          />
        )}
        <Button.Group>
          <Button
            basic={filters.source !== 'BILIK'}
            color="grey"
            type="button"
            onClick={(): void => {
              setFilters({
                source: filters.source === 'BILIK' ? undefined : 'BILIK',
                page: 1,
              });
            }}
          >
            Avis Bilik
          </Button>
          <Button
            basic={filters.source !== 'external'}
            color="grey"
            type="button"
            onClick={(): void => {
              setFilters({
                source: filters.source === 'external' ? undefined : 'external',
                page: 1,
              });
            }}
          >
            Avis externes
          </Button>
        </Button.Group>
        {/* only published (ok) reviews can have been verified, and pending
            (todo) reviews can be under verification: the filter only exists
            in those views */}
        {filters.status === 'ok' || filters.status === 'todo' ? (
          <CheckboxFilter
            checked={filters.hasCheckerId ? true : false}
            className="button basic grey"
            label={
              filters.status === 'ok'
                ? 'Avis vérifiés'
                : 'En cours de vérification'
            }
            onChange={(_, data): void => {
              setFilters({
                hasCheckerId: data.checked ? true : undefined,
                page: 1,
              });
            }}
          />
        ) : null}
        {filters.familyName || filters.givenName ? (
          <ActiveFilterChip
            onRemove={(): void => {
              setFilters({
                familyName: undefined,
                givenName: undefined,
              });
            }}
          >
            {filters.givenName} {filters.familyName}
          </ActiveFilterChip>
        ) : null}
        {filters.ip ? (
          <Button
            basic
            color="grey"
            content={filters.ip}
            icon={
              <Icon
                name="remove"
                onClick={(): void => setFilters({ ip: undefined })}
              />
            }
            labelPosition="left"
          />
        ) : null}
        {filters.id ? (
          <ActiveFilterChip
            onRemove={(): void => {
              setFilters({ id: undefined });
            }}
          >
            {`Avis : ${filters.id}`}
          </ActiveFilterChip>
        ) : null}
        {filters.allZones ? (
          <ActiveFilterChip
            onRemove={(): void => {
              setFilters({ allZones: undefined });
            }}
          >
            Toutes les zones
          </ActiveFilterChip>
        ) : null}
      </FilterRow>
    </>
  );
};
